import { useContext, useEffect, useRef } from "react";
import { UNSAFE_NavigationContext } from "react-router-dom";

// Tahan perpindahan selama `when` true (ada perubahan yang belum disimpan):
// - pindah menu/route (menu kiri, Link, navigate, tombol Back) lewat history.block (react-router 6.3 + history 5);
// - pindah tab Bootstrap (data-bs-toggle="tab", mis. Consumer/Kontraktor di Report Deposit): klik ditahan di
//   fase capture pada window, lalu diulang bila user setuju. Harus window, bukan document: Bootstrap 5.1 memasang
//   handler tab di document fase capture saat halaman dimuat, jadi handler di document akan kalah duluan.
// confirm() mengembalikan Promise<boolean>: true = boleh lanjut pindah.
const useUnsavedChangesGuard = (when, confirm) => {
  const { navigator } = useContext(UNSAFE_NavigationContext);
  const confirmRef = useRef(confirm);
  confirmRef.current = confirm;

  useEffect(() => {
    if (!when || typeof navigator.block !== "function") return undefined;

    let unblock = () => {};
    const block = () => {
      unblock = navigator.block((tx) => {
        const proceed = () => {
          unblock();
          tx.retry();
        };

        // Hanya ganti hash/query di halaman yang sama: tidak meninggalkan halaman
        if (tx.location.pathname === window.location.pathname) {
          proceed();
          block();
          return;
        }

        confirmRef.current().then((ok) => {
          if (ok) proceed();
        });
      });
    };
    block();

    return () => unblock();
  }, [when, navigator]);

  useEffect(() => {
    if (!when) return undefined;

    let bypass = false;
    const clickHandler = (e) => {
      const tab = !bypass && e.target.closest && e.target.closest('[data-bs-toggle="tab"]');
      if (!tab || tab.classList.contains("active")) return;

      e.preventDefault();
      e.stopPropagation();
      confirmRef.current().then((ok) => {
        if (!ok) return;
        bypass = true;
        tab.click();
        bypass = false;
      });
    };
    window.addEventListener("click", clickHandler, true);

    return () => window.removeEventListener("click", clickHandler, true);
  }, [when]);
};

export default useUnsavedChangesGuard;
